// Bar-level edits: add/delete bars, time & key signatures, clefs, tempo,
// repeats, endings, directions and other bar properties.

import type { Draft } from 'immer';
import { type Pitch, type DurBase, barCapacity, ticks, splitTicks, keyAlterFor } from '@simplenotation/theory';
import type { Score, BarInfo, Beat, TimeSignature, KeySignature, Clef, Direction, Barline, TrackBar } from '../model/types';
import { emptyTrackBar, createRest, newId } from '../model/factories';
import { timeSigAt, keySigAt } from '../model/query';
import { type EditorState, mutate, withError, focusBeat, selectionRange, transpositionFifths, retabKeepStrings } from './state';

/** Bar range covered by the selection (or the cursor bar). */
export function selectedBars(st: EditorState): { from: number; to: number } {
  const r = selectionRange(st);
  return { from: r.start.bar, to: r.end.bar };
}

function insertBarsDraft(d: Draft<Score>, at: number, count: number): void {
  // Effective signatures at the insertion point are kept on whichever bar ends up first.
  const ts = timeSigAt(d as Score, Math.max(0, Math.min(at, d.bars.length - 1)));
  const newBars: BarInfo[] = Array.from({ length: count }, () => ({}));
  if (at === 0) {
    const first = d.bars[0];
    newBars[0] = { timeSig: first.timeSig, keySig: first.keySig };
    delete first.timeSig;
    delete first.keySig;
  }
  if (at >= d.bars.length && d.bars[d.bars.length - 1]?.barline === 'final') {
    delete d.bars[d.bars.length - 1].barline;
    newBars[newBars.length - 1].barline = 'final';
  }
  d.bars.splice(at, 0, ...newBars);
  for (const t of d.tracks) {
    t.bars.splice(at, 0, ...Array.from({ length: count }, () => emptyTrackBar(ts)));
    if (t.clefChanges) {
      const moved: Record<number, Clef> = {};
      for (const [k, v] of Object.entries(t.clefChanges)) {
        const i = Number(k);
        moved[i >= at ? i + count : i] = v;
      }
      t.clefChanges = moved;
    }
  }
  for (const tm of d.tempos) if (tm.bar >= at && !(at === 0 && tm.bar === 0)) tm.bar += count;
}

export function insertBars(st: EditorState, side: 'before' | 'after', count = 1): EditorState {
  const { from, to } = selectedBars(st);
  const at = side === 'before' ? from : to + 1;
  const next = mutate(st, (d) => insertBarsDraft(d, at, count));
  return focusBeat(next, { ...st.cursor, bar: at, beat: 0 });
}

export function deleteBars(st: EditorState): EditorState {
  const { from, to } = selectedBars(st);
  const n = st.score.bars.length;
  if (to - from + 1 >= n) return withError(st, 'A score needs at least one bar.');
  const next = mutate(st, (d) => {
    const count = to - from + 1;
    // Carry signatures of deleted bars forward to the next remaining bar if it has none.
    const after = to + 1;
    if (after < d.bars.length) {
      const effTs = timeSigAt(d as Score, to);
      const effKs = keySigAt(d as Score, to);
      const prevTs = from > 0 ? timeSigAt(d as Score, from - 1) : null;
      const prevKs = from > 0 ? keySigAt(d as Score, from - 1) : null;
      const nb = d.bars[after];
      if (!nb.timeSig && (!prevTs || prevTs.num !== effTs.num || prevTs.den !== effTs.den)) nb.timeSig = { ...effTs };
      if (!nb.keySig && (!prevKs || prevKs.fifths !== effKs.fifths || prevKs.mode !== effKs.mode)) nb.keySig = { ...effKs };
    }
    const wasFinal = d.bars[to].barline === 'final' && to === d.bars.length - 1;
    d.bars.splice(from, count);
    if (wasFinal) d.bars[d.bars.length - 1].barline = 'final';
    if (!d.bars[0].timeSig) d.bars[0].timeSig = { num: 4, den: 4 };
    if (!d.bars[0].keySig) d.bars[0].keySig = { fifths: 0, mode: 'major' };
    for (const t of d.tracks) {
      t.bars.splice(from, count);
      if (t.clefChanges) {
        const moved: Record<number, Clef> = {};
        for (const [k, v] of Object.entries(t.clefChanges)) {
          const i = Number(k);
          if (i >= from && i <= to) continue;
          moved[i > to ? i - count : i] = v;
        }
        t.clefChanges = moved;
      }
    }
    d.tempos = d.tempos.filter((tm) => tm.bar < from || tm.bar > to || tm.bar === 0);
    for (const tm of d.tempos) if (tm.bar > to) tm.bar -= count;
    if (!d.tempos.some((tm) => tm.bar === 0)) d.tempos.unshift({ bar: 0, beatOffsetTicks: 0, bpm: 120, beatUnit: 4 });
  });
  const bar = Math.min(from, next.score.bars.length - 1);
  return focusBeat({ ...next, selection: null }, { ...st.cursor, bar, beat: 0 });
}

/**
 * Re-flow a voice's beats across bars after a time-signature change. Beats
 * crossing a barline are split into tied pieces (rests are split plainly).
 */
function rebarVoice(beats: Beat[], caps: (bar: number) => number): Beat[][] {
  const out: Beat[][] = [[]];
  let bar = 0;
  let filled = 0;
  for (const b of beats) {
    if (b.grace) {
      out[bar].push(b);
      continue;
    }
    let remaining = ticks(b.duration);
    let whole = true;
    while (remaining > 0) {
      const room = caps(bar) - filled;
      if (room <= 0) {
        bar++;
        filled = 0;
        out.push([]);
        continue;
      }
      if (whole && remaining <= room) {
        out[bar].push(b);
        filled += remaining;
        break;
      }
      const take = Math.min(room, remaining);
      remaining -= take;
      filled += take;
      const parts: Beat[] = splitTicks(take).map((pd) => ({
        ...b,
        id: newId(),
        duration: pd,
        notes: b.notes.map((n) => ({ ...n, id: newId(), tieToNext: true })),
      }));
      if (remaining === 0) {
        const last = parts[parts.length - 1];
        last.notes = last.notes.map((n, i) => {
          const m = { ...n };
          if (b.notes[i].tieToNext) m.tieToNext = true;
          else delete m.tieToNext;
          return m;
        });
      }
      out[bar].push(...parts);
      whole = false;
    }
  }
  return out;
}

export interface TimeSigOptions {
  scope: 'subsequent' | 'selected';
  rebar?: boolean;
}

export function setTimeSig(st: EditorState, ts: TimeSignature, opts: TimeSigOptions = { scope: 'subsequent' }): EditorState {
  const { from, to } = selectedBars(st);
  const next = mutate(st, (d) => {
    const prevAfter = timeSigAt(d as Score, to + 1 < d.bars.length ? to + 1 : to);
    const after = to + 1;
    if (opts.scope === 'selected' && after < d.bars.length && !d.bars[after].timeSig) {
      d.bars[after].timeSig = { ...prevAfter };
    }
    if (opts.scope === 'subsequent') {
      for (let i = from + 1; i < d.bars.length; i++) delete d.bars[i].timeSig;
    }
    d.bars[from].timeSig = { ...ts };
    // Drop a now-redundant signature right after the range.
    if (after < d.bars.length && d.bars[after].timeSig) {
      const a = d.bars[after].timeSig!;
      if (a.num === ts.num && a.den === ts.den && (a.symbol ?? '') === (ts.symbol ?? '')) delete d.bars[after].timeSig;
    }
    if (opts.rebar) {
      const end = opts.scope === 'selected' ? to : d.bars.length - 1;
      rebarRange(d, from, end);
    }
  });
  return focusBeat(next, { ...st.cursor, bar: Math.min(st.cursor.bar, next.score.bars.length - 1), beat: 0 });
}

/** Re-flow every track/voice of bars [from, to] into bars with their current capacities. */
function rebarRange(d: Draft<Score>, from: number, to: number): void {
  const capFor = (i: number) => barCapacity(timeSigAt(d as Score, from + i));
  const flows: Beat[][][][] = d.tracks.map((t) => {
    const maxVoices = Math.max(...t.bars.slice(from, to + 1).map((tb) => tb.voices.length));
    const vs: Beat[][][] = [];
    for (let v = 0; v < maxVoices; v++) {
      const all: Beat[] = [];
      for (let i = from; i <= to; i++) all.push(...((t.bars[i].voices[v] ?? []) as Beat[]));
      // Trailing rests beyond the last note are dropped so they don't create extra bars.
      while (all.length && all[all.length - 1].rest) all.pop();
      vs.push(all.length ? rebarVoice(JSON.parse(JSON.stringify(all)), capFor) : [[]]);
    }
    return vs;
  });
  let needed = to - from + 1;
  for (const vs of flows) for (const bars of vs) needed = Math.max(needed, bars.length);
  const oldCount = to - from + 1;
  if (needed > oldCount) {
    const insertAt = to + 1;
    const extra = needed - oldCount;
    const ts = timeSigAt(d as Score, to);
    d.bars.splice(insertAt, 0, ...Array.from({ length: extra }, () => ({}) as BarInfo));
    if (d.bars[insertAt - 1].barline === 'final' && insertAt + extra - 1 === d.bars.length - 1) {
      delete d.bars[insertAt - 1].barline;
      d.bars[d.bars.length - 1].barline = 'final';
    }
    for (const t of d.tracks) t.bars.splice(insertAt, 0, ...Array.from({ length: extra }, () => emptyTrackBar(ts)));
    for (const tm of d.tempos) if (tm.bar >= insertAt) tm.bar += extra;
  }
  d.tracks.forEach((t, ti) => {
    for (let i = 0; i < needed; i++) {
      const voices: Beat[][] = flows[ti].map((bars) => bars[i] ?? []);
      const tb: TrackBar = { voices: voices.length ? voices : [[]] };
      // Pad voice 0 with rests to the bar capacity.
      const cap = capFor(i);
      const v0 = tb.voices[0];
      const filled = v0.reduce((s, b) => s + (b.grace ? 0 : ticks(b.duration)), 0);
      if (filled < cap) for (const dd of splitTicks(cap - filled)) v0.push(createRest(dd));
      t.bars[from + i] = tb as Draft<TrackBar>;
    }
  });
}

export interface KeySigOptions {
  scope: 'subsequent' | 'selected';
  /** 'pitch': keep sounding pitches; 'appearance': keep notes on the same lines/spaces. */
  alter: 'pitch' | 'appearance';
}

export function setKeySig(st: EditorState, ks: KeySignature, opts: KeySigOptions = { scope: 'subsequent', alter: 'pitch' }): EditorState {
  const { from, to } = selectedBars(st);
  const next = mutate(st, (d) => {
    const n = d.bars.length;
    // Extent of the change.
    let end = opts.scope === 'selected' ? to : n - 1;
    if (opts.scope === 'subsequent') {
      // Up to the next explicit key change.
      for (let i = from + 1; i < n; i++) {
        if (d.bars[i].keySig) {
          end = i - 1;
          break;
        }
      }
    }
    const oldKeys: KeySignature[] = [];
    for (let i = from; i <= end; i++) oldKeys.push(keySigAt(d as Score, i));
    const restore = end + 1 < n && !d.bars[end + 1].keySig ? keySigAt(d as Score, end + 1) : null;
    d.bars[from].keySig = { ...ks };
    for (let i = from + 1; i <= end; i++) delete d.bars[i].keySig;
    if (restore && (restore.fifths !== ks.fifths || restore.mode !== ks.mode)) d.bars[end + 1].keySig = { ...restore };
    if (opts.alter === 'appearance') {
      d.tracks.forEach((t, ti) => {
        const shift = transpositionFifths(t.writtenToSounding);
        for (let i = from; i <= end; i++) {
          const oldF = oldKeys[i - from].fifths + shift;
          const newF = ks.fifths + shift;
          t.bars[i].voices.forEach((v, vi) => {
            v.forEach((b, bi) => {
              if (b.rest) return;
              for (const note of b.notes) {
                const p = note.pitch as Pitch;
                const delta = keyAlterFor(newF, p.step) - keyAlterFor(oldF, p.step);
                const a = p.alter + delta;
                if (a >= -2 && a <= 2) note.pitch = { ...p, alter: a as Pitch['alter'] };
              }
              retabKeepStrings(d, { track: ti, bar: i, voice: vi, beat: bi });
            });
          });
        }
      });
    }
  });
  return next;
}

export function setClef(st: EditorState, clef: Clef): EditorState {
  const c = st.cursor;
  const { from } = selectedBars(st);
  return mutate(st, (d) => {
    const t = d.tracks[c.track];
    if (from === 0) {
      t.clef = clef;
    } else {
      t.clefChanges = { ...(t.clefChanges ?? {}), [from]: clef };
    }
  });
}

export function setTempo(st: EditorState, mark: { bpm: number; beatUnit?: DurBase; dotted?: boolean; text?: string }, bar?: number): EditorState {
  const b = bar ?? selectedBars(st).from;
  const bpm = Math.max(10, Math.min(400, Math.round(mark.bpm)));
  return mutate(st, (d) => {
    const existing = d.tempos.find((t) => t.bar === b && t.beatOffsetTicks === 0);
    const tm = { bar: b, beatOffsetTicks: 0, bpm, beatUnit: mark.beatUnit ?? 4, dotted: mark.dotted || undefined, text: mark.text || undefined };
    if (existing) Object.assign(existing, tm);
    else d.tempos.push(tm);
    d.tempos.sort((a, x) => a.bar - x.bar || a.beatOffsetTicks - x.beatOffsetTicks);
  });
}

export function removeTempo(st: EditorState, bar: number): EditorState {
  if (bar === 0) return st;
  return mutate(st, (d) => {
    d.tempos = d.tempos.filter((t) => t.bar !== bar);
  });
}

/** Tempo of the first mark (the transport's BPM display). */
export function nudgeMainTempo(st: EditorState, delta: number): EditorState {
  const first = st.score.tempos[0];
  return setTempo(st, { bpm: (first?.bpm ?? 120) + delta, beatUnit: first?.beatUnit, dotted: first?.dotted, text: first?.text }, 0);
}

function mapBars(st: EditorState, f: (b: Draft<BarInfo>, i: number) => void, range = selectedBars(st)): EditorState {
  return mutate(st, (d) => {
    for (let i = range.from; i <= range.to; i++) f(d.bars[i], i);
  });
}

export function setTripletFeel(st: EditorState, feel: 'none' | '8th' | '16th'): EditorState {
  const { from } = selectedBars(st);
  return mapBars(st, (b) => {
    if (feel === 'none' && from === 0) delete b.tripletFeel;
    else b.tripletFeel = feel;
  }, { from, to: from });
}

export function toggleBarline(st: EditorState, type: Barline): EditorState {
  const { to } = selectedBars(st);
  const cur = st.score.bars[to].barline;
  return mapBars(st, (b) => {
    if (cur === type) delete b.barline;
    else b.barline = type;
  }, { from: to, to });
}

export function toggleRepeatStart(st: EditorState): EditorState {
  const { from } = selectedBars(st);
  return mapBars(st, (b) => {
    if (b.repeatStart) delete b.repeatStart;
    else b.repeatStart = true;
  }, { from, to: from });
}

export function toggleRepeatEnd(st: EditorState, times = 2): EditorState {
  const { to } = selectedBars(st);
  return mapBars(st, (b) => {
    if (b.repeatEnd) delete b.repeatEnd;
    else b.repeatEnd = Math.max(2, times);
  }, { from: to, to });
}

export function setEndings(st: EditorState, endings: number[]): EditorState {
  return mapBars(st, (b) => {
    if (endings.length) b.endings = [...endings].sort((a, x) => a - x);
    else delete b.endings;
  });
}

export function setDirection(st: EditorState, dir: Direction | null): EditorState {
  const { from } = selectedBars(st);
  return mapBars(st, (b) => {
    if (dir) b.direction = dir;
    else delete b.direction;
  }, { from, to: from });
}

export function togglePickup(st: EditorState): EditorState {
  return mapBars(st, (b) => {
    if (b.pickup) delete b.pickup;
    else b.pickup = true;
  }, { from: 0, to: 0 });
}

export function setMultiRest(st: EditorState, count: number | null): EditorState {
  const { from } = selectedBars(st);
  return mapBars(st, (b) => {
    if (count && count > 1) b.multiRest = Math.min(999, Math.round(count));
    else delete b.multiRest;
  }, { from, to: from });
}

export function setBarNumberOverride(st: EditorState, n: number | null): EditorState {
  const { from } = selectedBars(st);
  return mapBars(st, (b) => {
    if (n === null || Number.isNaN(n)) delete b.numberOverride;
    else b.numberOverride = Math.round(n);
  }, { from, to: from });
}

export function setSection(st: EditorState, section: { name?: string; letter?: string } | null): EditorState {
  const { from } = selectedBars(st);
  return mapBars(st, (b) => {
    const s = { ...(b.section ?? {}), ...(section ?? {}) };
    if (!section) {
      delete b.section;
      return;
    }
    if (!s.name) delete s.name;
    if (!s.letter) delete s.letter;
    if (s.name || s.letter) b.section = s;
    else delete b.section;
  }, { from, to: from });
}

export function setLineBreak(st: EditorState, kind: 'force' | 'prevent'): EditorState {
  const { to } = selectedBars(st);
  return mapBars(st, (b) => {
    if (b.lineBreak === kind) delete b.lineBreak;
    else b.lineBreak = kind;
  }, { from: to, to });
}

export function setCommentary(st: EditorState, text: string): EditorState {
  const { from } = selectedBars(st);
  return mapBars(st, (b) => {
    if (text) b.commentary = text;
    else delete b.commentary;
  }, { from, to: from });
}

export function setSimile(st: EditorState, n: 1 | 2): EditorState {
  const { from } = selectedBars(st);
  return mapBars(st, (b) => {
    if (b.simile === n) delete b.simile;
    else b.simile = n;
  }, { from, to: from });
}

/** Rest beats for a whole bar in the cursor track/voice (clears the bar). */
export function clearBars(st: EditorState): EditorState {
  const { from, to } = selectedBars(st);
  const c = st.cursor;
  const next = mutate(st, (d) => {
    for (let i = from; i <= to; i++) {
      const ts = timeSigAt(d as Score, i);
      d.tracks[c.track].bars[i] = emptyTrackBar(ts) as Draft<TrackBar>;
    }
  });
  return focusBeat({ ...next, selection: null }, { ...c, bar: from, beat: 0, voice: 0 });
}

